import { useState, type FormEvent } from "react";
import PageHeader from "@/components/feature/PageHeader";

const formSubmitAddr = "https://readdy.ai/api/form/da2k9kbd3r93mn1k3ahg";

interface Status {
  type: "idle" | "loading" | "success" | "error";
  msg: string;
}

export default function ContactPage() {
  const [status, setStatus] = useState<Status>({ type: "idle", msg: "" });

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const hp = (formData.get("phone_alt") as string) || "";
    if (hp.trim()) {
      setStatus({ type: "success", msg: "已收到你的留言，我们会尽快与你联系。" });
      form.reset();
      return;
    }
    formData.delete("phone_alt");

    setStatus({ type: "loading", msg: "正在提交…" });
    try {
      const res = await fetch(formSubmitAddr, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const msg =
        parsed?.meta?.message ||
        parsed?.meta?.detail ||
        parsed?.message ||
        (res.ok ? "已收到你的留言，我们会尽快与你联系。" : "提交失败，请稍后再试。");
      if (res.ok && parsed?.code === "OK") {
        setStatus({ type: "success", msg: "已收到你的留言，我们会尽快与你联系。" });
        form.reset();
      } else {
        setStatus({ type: "error", msg: msg.includes("spam") ? "提交失败，请稍后再试。" : msg });
      }
    } catch {
      setStatus({ type: "error", msg: "网络异常，提交失败，请稍后再试。" });
    }
  }

  return (
    <>
      <PageHeader
        title="联系我们"
        subtitle="CONTACT US"
        desc="资源收录、内容合作、意见反馈，或是有任何想说的，都可以通过下方表单告诉我们。我们会在收到后尽快回复。"
        crumbs={[{ label: "联系我们" }]}
      />

      <section className="mx-auto max-w-7xl px-4 md:px-6 py-8 md:py-12">
        <div className="grid lg:grid-cols-5 gap-8 lg:gap-12 items-start">
          <div className="lg:col-span-2">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950">欢迎来信</h2>
            <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
              无论是推荐靠谱的韩国免费漫画资源站、提出改进建议，还是洽谈合作，我们都很乐意听到你的声音。
            </p>
            <div className="mt-6 space-y-4">
              <div className="flex items-center gap-3">
                <span className="w-11 h-11 rounded-full bg-primary-100/60 text-primary-500 flex items-center justify-center text-xl"><i className="ri-mail-fill"></i></span>
                <div>
                  <p className="text-xs text-foreground-600">电子邮箱</p>
                  <p className="text-sm text-foreground-950">support@hanguofeifei.com</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="w-11 h-11 rounded-full bg-secondary-100/70 text-secondary-500 flex items-center justify-center text-xl"><i className="ri-chat-3-fill"></i></span>
                <div>
                  <p className="text-xs text-foreground-600">反馈内容</p>
                  <p className="text-sm text-foreground-950">资源推荐 / 内容合作 / 意见建议</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="w-11 h-11 rounded-full bg-accent-500/15 text-accent-500 flex items-center justify-center text-xl"><i className="ri-time-fill"></i></span>
                <div>
                  <p className="text-xs text-foreground-600">回复时间</p>
                  <p className="text-sm text-foreground-950">工作日 48 小时内回复</p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-3 rounded-xl bg-background-100 border border-background-200/70 p-6 md:p-8">
            <form id="contact-form" data-readdy-form onSubmit={onSubmit} className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="name" className="block text-xs font-bold text-foreground-800 mb-1.5">你的称呼</label>
                  <input id="name" name="name" type="text" required maxLength={30}
                    className="w-full px-3.5 py-2.5 rounded-md bg-background-200/60 border border-background-300/60 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none focus:border-primary-400"
                    placeholder="怎么称呼你" />
                </div>
                <div>
                  <label htmlFor="email" className="block text-xs font-bold text-foreground-800 mb-1.5">联系邮箱</label>
                  <input id="email" name="email" type="email" required maxLength={80}
                    className="w-full px-3.5 py-2.5 rounded-md bg-background-200/60 border border-background-300/60 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none focus:border-primary-400"
                    placeholder="name@example.com" />
                </div>
              </div>
              <div>
                <label htmlFor="topic" className="block text-xs font-bold text-foreground-800 mb-1.5">反馈主题</label>
                <select id="topic" name="topic"
                  className="w-full px-3.5 py-2.5 rounded-md bg-background-200/60 border border-background-300/60 text-sm text-foreground-950 outline-none focus:border-primary-400">
                  <option>资源推荐</option>
                  <option>内容合作</option>
                  <option>意见反馈</option>
                  <option>其他问题</option>
                </select>
              </div>
              <div>
                <label htmlFor="message" className="block text-xs font-bold text-foreground-800 mb-1.5">留言内容</label>
                <textarea id="message" name="message" required rows={5} maxLength={500}
                  className="w-full px-3.5 py-2.5 rounded-md bg-background-200/60 border border-background-300/60 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none focus:border-primary-400 resize-none"
                  placeholder="想和我们说什么…"></textarea>
                <p className="mt-1 text-[11px] text-foreground-600 text-right">最多 500 字</p>
              </div>
              <input type="text" name="phone_alt" className="hp-field" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
              <button type="submit" disabled={status.type === "loading"}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 font-bold text-sm whitespace-nowrap hover:bg-primary-600 transition-colors disabled:opacity-60">
                <i className="ri-send-plane-fill"></i>
                {status.type === "loading" ? "提交中…" : "提交留言"}
              </button>
              {status.type === "success" && (
                <p className="flex items-center gap-2 text-sm text-secondary-500"><i className="ri-checkbox-circle-fill"></i>{status.msg}</p>
              )}
              {status.type === "error" && (
                <p className="flex items-center gap-2 text-sm text-primary-600"><i className="ri-error-warning-fill"></i>{status.msg}</p>
              )}
            </form>
          </div>
        </div>
      </section>
    </>
  );
}